/**
 * Marketing Flex Table — the facts the page pivots locally.
 *
 * The API sends one period's records already grouped on every dimension each
 * record family carries, with labels sent once in `dictionaries` and fact rows
 * holding ids. The page regroups those rows for whatever dimensions, metrics,
 * sort and search the user picks, so only a period or filter change refetches.
 *
 * MONEY IS INTEGER CENTS HERE, not a decimal string as in the other standard
 * reports. Every figure in this table is a sum the browser re-adds for each
 * grouping, and integer cents add exactly (well inside 2^53), where decimal
 * strings would have to be re-parsed per cell.
 *
 * Id 0 means "Not set" in every dimension column. The column order of each
 * row is fixed by the *_COLUMNS tuples below and mirrored, in the same order,
 * by `App\Services\Reports\FlexTable\FlexFactsEncoder` — change both together.
 */

/** 1 = New, 2 = Existing; see R16. */
export type FlexPatientType = 1 | 2;

export const PATIENT_TYPE_NEW: FlexPatientType = 1;
export const PATIENT_TYPE_EXISTING: FlexPatientType = 2;

/** `YYYY-MM-DD`, a Pacific calendar day. Parse with `T00:00:00` appended. */
export type FlexDayKey = string;

export const LEAD_COLUMNS = [
  'day',
  'sourceId',
  'facilityId',
  'patientType',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'planTypeId',
  'leads',
  'converted',
] as const;

export const APPOINTMENT_COLUMNS = [
  'day',
  'sourceId',
  'facilityId',
  'patientType',
  'planTypeId',
  'appointmentTypeId',
  'booked',
  'showed',
  'noShows',
  'cancelled',
] as const;

export const NEW_PATIENT_COLUMNS = ['day', 'sourceId', 'facilityId', 'planTypeId', 'newPatients'] as const;

export const PAYMENT_COLUMNS = ['day', 'sourceId', 'facilityId', 'patientType', 'planTypeId', 'revenueCents'] as const;

export const NEW_CUSTOMER_COLUMNS = ['day', 'sourceId', 'facilityId', 'planTypeId', 'newCustomers'] as const;

/** [day, sourceId, facilityId, patientType, utm×5 (indexes into dictionaries.utm), planTypeId, leads, converted] */
export type FlexLeadFact = [FlexDayKey, number, number, FlexPatientType, number, number, number, number, number, number, number, number];

/** [day, sourceId, facilityId, patientType, planTypeId, appointmentTypeId, booked, showed, noShows, cancelled] */
export type FlexAppointmentFact = [FlexDayKey, number, number, FlexPatientType, number, number, number, number, number, number];

/** [day, sourceId, facilityId, planTypeId, newPatients] — a new patient is always New. */
export type FlexNewPatientFact = [FlexDayKey, number, number, number, number];

/** [day, sourceId, facilityId, patientType, planTypeId, revenueCents] */
export type FlexPaymentFact = [FlexDayKey, number, number, FlexPatientType, number, number];

/** [day, sourceId, facilityId, planTypeId, newCustomers] — a first paid purchase, always New. */
export type FlexNewCustomerFact = [FlexDayKey, number, number, number, number];

export interface FlexSource {
  name: string;
  /** The Lead Source Category it belongs to; 0 when it has none. */
  categoryId: number;
}

export interface FlexDictionaries {
  sources: Record<string, FlexSource>;
  categories: Record<string, string>;
  facilities: Record<string, string>;
  planTypes: Record<string, string>;
  appointmentTypes: Record<string, string>;
  /** UTM values, shared by all five UTM columns. Index 0 is always '' (Not set). */
  utm: string[];
}

export interface FlexFacts {
  period: { from: string; to: string };
  dictionaries: FlexDictionaries;
  leads: FlexLeadFact[];
  appointments: FlexAppointmentFact[];
  newPatients: FlexNewPatientFact[];
  payments: FlexPaymentFact[];
  /** Absent from an API that predates the family; read as empty. */
  newCustomers?: FlexNewCustomerFact[];
}
